<!--
  Home: one button, no decisions.

  The app picks the day. Under it, one line saying what he did last. The second,
  smaller button is the fifteen-minute version, because the pattern that killed
  the last app was "full session or nothing".
-->
<script lang="ts">
  import { app, chooseNextDay } from '../lib/state.svelte';
  import { whenShort } from '../lib/format';

  const choice = $derived(chooseNextDay(app.bootstrap?.plan ?? null, app.history));
  const day = $derived(choice.day);
  const last = $derived(choice.last);
  const resumable = $derived(app.resumable);
</script>

<div class="home">
  <header>
    <h1>Lift</h1>
    <button class="link" onclick={() => app.go('history')}>History</button>
  </header>

  <main>
    {#if resumable}
      <div class="resume">
        <p>{resumable.day_name} is still open from {whenShort(resumable.started_at)}.</p>
        <div class="resume-actions">
          <button class="primary" onclick={() => app.resume(resumable)}>Carry on</button>
          <button class="link" onclick={() => app.discardResumable()}>Close it</button>
        </div>
      </div>
    {:else if day}
      <button class="start" onclick={() => app.start(day, 'full')}>
        <span class="start-label">Start</span>
        <span class="start-day">{day.name.replace(/^Day \d+ - /, '')}</span>
      </button>

      <!-- The highest-leverage button here: a third option between everything
           and nothing. Three compound sets, logged as a real session. -->
      <button class="short" onclick={() => app.start(day, 'short')}>
        Low energy - give me 15 minutes
      </button>

      {#if last}
        <p class="last">
          Last: {last.day_name ?? 'a session'}, {whenShort(last.started_at)}
        </p>
      {:else}
        <p class="last">Nothing logged yet.</p>
      {/if}
    {:else if app.bootstrap === null}
      <p class="muted">Loading the plan…</p>
    {:else}
      <p class="muted">No active plan. Apply the seed migration.</p>
    {/if}
  </main>
</div>

<style>
  .home {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    padding: env(safe-area-inset-top) 16px calc(env(safe-area-inset-bottom) + 24px);
  }

  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 18px 0;
  }

  h1 {
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--dim);
    margin: 0;
  }

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 14px;
  }

  .start {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    text-align: left;
    background: var(--accent);
    color: var(--on-accent);
    border: 0;
    border-radius: 18px;
    padding: 26px 24px;
  }

  .start-label {
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.7;
  }

  .start-day {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
  }

  .short {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 14px;
    color: var(--text);
    font-size: 0.95rem;
    padding: 16px;
  }

  .last {
    color: var(--dim);
    font-size: 0.9rem;
    margin: 6px 0 0;
    text-align: center;
  }

  .muted {
    color: var(--dim);
    text-align: center;
  }

  .resume {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 16px;
    padding: 20px;
  }

  .resume p {
    margin: 0 0 14px;
  }

  .resume-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .primary {
    background: var(--accent);
    color: var(--on-accent);
    border: 0;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    padding: 14px 20px;
  }

  .link {
    background: none;
    border: 0;
    color: var(--dim);
    font-size: 0.9rem;
    padding: 8px;
    text-decoration: underline;
  }
</style>
